import {
    AirTerminalInstallation,
    BaseComponent,
    BlueprintLocation,
    Building,
    ColorModel,
    ComponentDocument,
    ConditionModel,
    ElectricalInstallation,
    FloorAreaVerification,
    HazardousModel,
    LevelModel,
    PhotoLocation,
    PhotoModel,
    RadiatorInstallation,
    RiskConstruction,
    RoofAccessPoint,
    RoofAnchorPoint,
    RoomModel,
    ValueStatus,
    ValueWithMetadata,
    WorkEnvironmentRisk
} from "./protocols";
import {
    BaseType,
    CardinalDirection,
    ElevatorType,
    RoofSafetyStatus,
    RoofShape,
    RoomType,
    UtilityCategory,
    OutdoorCategory
} from "./Enums";
import { translate } from "./EnumTranslations";
import { AnyGeometryShape, computeShapeArea } from "./GeometryShapes";
import { getCustomTypeDisplayName, formatLevelNumber } from "../Helpers/utils";
import { resolveBuildComponentTypeTranslation } from "../Helpers/BuildComponentTypesStore";
import { composeComponentDisplayName } from "../Helpers/componentDisplayName";
import { formatDateForStorage } from "../Helpers/DateUtils";
import { levelDisplayName } from "../Helpers/levelDisplayName";

/**
 * Creates a ValueWithMetadata object with the current timestamp and user
 * @param value The value to wrap
 * @param userUid The UID of the user making the change
 * @param status The status of the value (defaults to 'set')
 */
export function createValueWithMetadata<T>(
    value: T | null,
    userUid: string | null,
    status: ValueStatus = ValueStatus.set
): ValueWithMetadata<T> {
    return {
        value,
        updatedBy: userUid,
        updatedWhen: formatDateForStorage(new Date()),
        status
    };
}

export class BaseComponentImpl implements BaseComponent {
    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: ValueWithMetadata<string>;
    condition?: ValueWithMetadata<ConditionModel>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<string[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    thickness?: ValueWithMetadata<number>;
    width?: ValueWithMetadata<number>;
    height?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    linearMeters?: ValueWithMetadata<number>;
    label?: ValueWithMetadata<string>;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    canHaveLock?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    roomKey?: ValueWithMetadata<string>;
    utilityCategory?: UtilityCategory;
    outdoorCategory?: OutdoorCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;
    brand?: ValueWithMetadata<string>;
    mapLocations?: { [key: string]: BlueprintLocation };
    blueprintLocations?: { [key: string]: BlueprintLocation };
    photoLocations?: { [photoKey: string]: PhotoLocation };
    sequentialNumber?: number;

    // Warranty tracking fields
    warrantyStartDate?: ValueWithMetadata<string>;
    warrantyDurationYears?: ValueWithMetadata<number>;

    // Component documents (manuals, receipts, certificates)
    documents?: { [key: string]: ComponentDocument };

    // Archive and replacement tracking
    archivedAt?: string;
    archivedBy?: string;
    replacedFromKey?: string;
    replacedByKey?: string;

    parentSurface?: ValueWithMetadata<string>;
    parentSurface1?: ValueWithMetadata<string>;
    parentSurface2?: ValueWithMetadata<string>;
    parentKey?: ValueWithMetadata<string>;

    // RoofSafety-specific fields (BaseType.RoofSafety)
    safetyStatus?: ValueWithMetadata<RoofSafetyStatus>;
    anchorPoints?: ValueWithMetadata<{ [key: string]: RoofAnchorPoint }>;
    accessInfo?: ValueWithMetadata<string>;
    accessPositions?: ValueWithMetadata<{ [key: string]: RoofAccessPoint }>;
    parentComponentKey?: string;

    // GPS coordinates for exterior components
    gpsCoordinates?: ValueWithMetadata<{ latitude: number; longitude: number }>;

    // Window-specific fields (BaseType.Window)
    uvValue?: ValueWithMetadata<number>;  // U-value (thermal insulation)

    constructor(data: any) {
        // Required fields
        this.key = data.key;
        this.type = data.type;
        this.createdBy = data.createdBy;
        this.createdWhen = data.createdWhen;
        this.comment = data.comment;

        // Optional fields - use Object.assign for cleaner assignment
        const optionalFields = [
            'photos', 'specificType', 'condition', 'buildingRisks', 'material',
            'color', 'areaShapes', 'cardinalDirection', 'depth', 'thickness', 'width', 'height', 'insulation',
            'linearMeters', 'label', 'hazardous', 'canHaveWindows', 'canHaveDoors',
            'canHaveLock',
            'roofSlope', 'roofShape', 'parentRoomKeys', 'roomKey',
            'utilityCategory', 'outdoorCategory', 'elevatorType', 'brand', 'mapLocations', 'blueprintLocations',
            'parentSurface', 'parentSurface1', 'parentSurface2', 'parentKey',
            'photoLocations', 'sequentialNumber',
            // Warranty fields
            'warrantyStartDate', 'warrantyDurationYears',
            // Component documents
            'documents',
            // Archive and replacement fields
            'archivedAt', 'archivedBy', 'replacedFromKey', 'replacedByKey',
            // RoofSafety fields
            'safetyStatus', 'anchorPoints', 'accessInfo', 'accessPositions', 'parentComponentKey',
            // GPS coordinates
            'gpsCoordinates',
            // Window-specific fields
            'uvValue'
        ];

        for (const field of optionalFields) {
            if (data[field] !== undefined) {
                (this as any)[field] = data[field];
            }
        }
    }

    /** The shared naming rule lives in Helpers/componentDisplayName.ts (mirrored by iOS and functions). */
    // `building` is unused; kept so existing callers compile.
    displayName(building?: Building): string {
        return composeComponentDisplayName({
            label: this.label?.value,
            isWindow: this.type === BaseType.Window,
            sequentialNumber: this.sequentialNumber,
            roofShapeName: this.roofShape?.value ? translate(this.roofShape.value) : undefined,
            specificTypeName: resolveBuildComponentTypeTranslation(this.specificType?.value),
            baseTypeName: translate(this.type),
            cardinalDirectionName: this.cardinalDirection?.value ? translate(this.cardinalDirection.value) : undefined
        });
    }

    /**
     * Calculates the total area of all shapes in areaShapes.
     * All components (including windows/doors) use areaShapes uniformly.
     * @param building The building object (unused, kept for compatibility)
     * @returns The sum of all shape areas or null if no areas are set
     */
    totalArea(building: Building): number | null {
        if (!this.areaShapes || !this.areaShapes.value || this.areaShapes.value.length === 0) {
            return null;
        }

        let totalArea = 0;
        for (const shape of this.areaShapes.value) {
            totalArea += computeShapeArea(shape);
        }

        return totalArea;
    }

    /**
     * Calculates the total area excluding windows and doors that are attached to this component
     * @param building The building object to look up child components
     * @returns The area excluding windows and doors, or null if no area is set
     */
    totalAreaExcludingWindowsAndDoors(building: Building): number | null {
        const totalArea = this.totalArea(building);
        if (totalArea === null) {
            return null;
        }

        // Find all windows and doors that have this component as a parent
        let windowsAndDoorsArea = 0;

        if (building.model?.structure?.components) {
            Object.values(building.model.structure.components).forEach(component => {
                if (component.type === BaseType.Window || component.type === BaseType.Door) {
                    // Check all possible parent surface properties
                    const parentSurfaces = [
                        (component as any).parentSurface?.value,
                        (component as any).parentSurface1?.value,
                        (component as any).parentSurface2?.value
                    ].filter(Boolean);

                    if (parentSurfaces.includes(this.key)) {
                        const childComponent = new BaseComponentImpl(component);
                        const childArea = childComponent.totalArea(building);
                        if (childArea !== null) {
                            windowsAndDoorsArea += childArea;
                        }
                    }
                }
            });
        }

        return totalArea - windowsAndDoorsArea;
    }

    /**
     * Returns the most recent date across creation timestamp, all ValueWithMetadata fields, and attached photos, formatted as YYYY-MM-DD
     * @returns Latest date string
     */
    lastUpdated(): string {
        const dates: string[] = [];

        // Component's creation timestamp is mandatory
        dates.push(this.createdWhen);

        // Collect dates from all ValueWithMetadata fields
        const fields: Array<ValueWithMetadata<any> | undefined> = [
            this.comment,
            this.specificType,
            this.label,
            this.roomKey,
            this.condition,
            this.buildingRisks,
            this.material,
            this.color,
            this.areaShapes,
            this.cardinalDirection,
            this.depth,
            this.thickness,
            this.width,
            this.height,
            this.insulation,
            this.roofSlope,
            this.roofShape,
            this.parentRoomKeys,
            this.warrantyStartDate,
            this.warrantyDurationYears
        ];

        for (const field of fields) {
            if (field?.updatedWhen) {
                dates.push(field.updatedWhen);
            }
        }

        // Include upload dates from photos
        if (this.photos) {
            for (const photo of Object.values(this.photos)) {
                if (photo.date) {
                    dates.push(photo.date);
                }
            }
        }

        // Determine the latest date-time
        const latest = dates
            .map(dateStr => new Date(dateStr))
            .reduce((prev, curr) => prev > curr ? prev : curr);

        // Format as YYYY-MM-DD
        const year = latest.getFullYear();
        const month = String(latest.getMonth() + 1).padStart(2, '0');
        const day = String(latest.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}

export class RoomImpl implements RoomModel {
    key: string = "";
    number?: string;
    label?: string;
    levelKey?: string;
    levelKeys?: { [levelKey: string]: true };
    location?: BlueprintLocation;
    blueprintLocations?: { [key: string]: BlueprintLocation };
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
    photos?: { [key: string]: PhotoModel };
    electricalInstallation?: ValueWithMetadata<ElectricalInstallation>;
    airTerminalInstallation?: ValueWithMetadata<AirTerminalInstallation>;
    radiatorInstallation?: ValueWithMetadata<RadiatorInstallation>;
    workEnvironmentRisk?: WorkEnvironmentRisk;
    hasPoorReception?: boolean;
    floorAreaVerification?: FloorAreaVerification;

    constructor(data: RoomModel) {
        this.ceilingHeight = new ValueWithMetadataImpl<number>();
        Object.assign(this, data);

        // Migration logic: if old location exists but blueprintLocations doesn't,
        // create blueprintLocations with a default blueprint
        if (this.location && !this.blueprintLocations) {
            this.blueprintLocations = {
                'default': this.location
            };
        }
    }

    displayName(): string {
        if (this.label && this.number) {
            return `Rum ${this.number} - ${this.label}`
        }
        if (this.number) {
            return `Rum ${this.number}`
        }
        if (this.label) {
            return this.label
        }
        if (this.type) {
            return translate(this.type)
        }
        return "Odefinierat rum"
    }

    /**
     * Get the blueprint location for a specific blueprint key
     */
    getBlueprintLocation(blueprintKey: string): BlueprintLocation | undefined {
        return this.blueprintLocations?.[blueprintKey];
    }

    /**
     * Set the blueprint location for a specific blueprint key
     */
    setBlueprintLocation(blueprintKey: string, location: BlueprintLocation): void {
        if (!this.blueprintLocations) {
            this.blueprintLocations = {};
        }
        this.blueprintLocations[blueprintKey] = location;
    }

    /**
     * Remove the blueprint location for a specific blueprint key
     */
    removeBlueprintLocation(blueprintKey: string): void {
        if (this.blueprintLocations) {
            delete this.blueprintLocations[blueprintKey];
        }
    }

    /**
     * Get all blueprint keys that this room has locations for
     */
    getAvailableBlueprintKeys(): string[] {
        return this.blueprintLocations ? Object.keys(this.blueprintLocations) : [];
    }

    /**
     * Check if room has a location for a specific blueprint
     */
    hasLocationForBlueprint(blueprintKey: string): boolean {
        return !!(this.blueprintLocations && this.blueprintLocations[blueprintKey]);
    }
}

export class LevelImpl implements LevelModel {
    key: string;
    name?: string;
    number: number;
    ceilingHeight?: number;
    knownArea?: number;
    type?: any;
    currentBlueprintKey?: string;
    blueprints?: { [key: string]: PhotoModel };

    constructor(data: LevelModel) {
        this.key = data.key;
        this.number = data.number;
        Object.assign(this, data);
    }

    displayName(): string {
        return levelDisplayName(this);
    }

    formattedNumber(): string {
        return formatLevelNumber(this.number);
    }
}

export class ValueWithMetadataImpl<T> implements ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;

    constructor(value: T | null = null, updatedWhen: string | null = null,
        updatedBy: string | null = null, status: ValueStatus = ValueStatus.notSet) {
        this.value = value;
        this.updatedWhen = updatedWhen;
        this.updatedBy = updatedBy;
        this.status = status;
    }
}